/**
 * The stored-policy picker of the SAS dialog with its prerequisite read made visible (UI/UX fix round A, review A6):
 * Loading, an Error with Retry, an Empty list that says where policies are added, and only then the list. The dialog
 * keeps Generate unavailable until a policy is chosen.
 */
import { Button } from '../../../../../../components/ui/button';
import type { StorageAccessPolicy, StorageOperationError } from '../../../../../../types/azureStorage';

type PolicyRead = { data?: { policies: StorageAccessPolicy[] }; isLoading: boolean; error?: StorageOperationError; reload: () => void };

export function SasPolicyPicker({ acl, value, onChange, ownerLabel }: { acl: PolicyRead; value: string; onChange: (identifier: string) => void; ownerLabel: string }) {
  if (acl.error) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs" role="alert" data-testid="storage-sas-policies-error">
        <span className="text-destructive">Stored access policies could not be read: {acl.error.message}</span>
        <Button type="button" size="xs" variant="outline" onClick={acl.reload} data-testid="storage-sas-policies-retry">Retry</Button>
      </div>
    );
  }
  if (acl.isLoading || !acl.data) return <p className="text-xs text-muted-foreground" data-testid="storage-sas-policies-loading">Loading the stored access policies of {ownerLabel}…</p>;
  if (acl.data.policies.length === 0) {
    return <p className="text-xs text-muted-foreground" data-testid="storage-sas-policies-empty">{ownerLabel} has no stored access policies. Add one with Manage access policies… on it, or use Ad-hoc.</p>;
  }
  return (
    <label className="text-sm">Stored policy identifier
      <select aria-label="Stored policy identifier" className="ml-2 h-8 rounded border border-input bg-background px-2" value={value} onChange={(event) => onChange(event.target.value)} data-testid="storage-sas-policy-select">
        <option value="">Choose…</option>
        {acl.data.policies.map((policy) => <option key={policy.id} value={policy.id}>{policy.id}</option>)}
      </select>
    </label>
  );
}
